// Updated ConfidenceSection.jsx (dynamic with props + fallback icons)
import { ShieldCheck, Lock, ClipboardCheck, CheckCircle } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'

// Fallback icon logic based on step index or title keywords
const getFallbackIcon = (title, index) => {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('secure') || lowerTitle.includes('verified') || index === 0) {
    return ShieldCheck;
  }
  if (lowerTitle.includes('protect') || lowerTitle.includes('payment') || index === 1) {
    return Lock;
  }
  if (lowerTitle.includes('document') || lowerTitle.includes('inspect') || index === 2) {
    return ClipboardCheck;
  }
  
  // Default fallback
  return CheckCircle;
};

export function ConfidenceSection({ header, steps, translate }) {
  // Fallback data if props are empty (from translations)
  const fallbackHeader = {
    title: translate('newBuyer.confidence.title') || 'Buy with Absolute Confidence',
    description: translate('newBuyer.confidence.subtitle') || 'We prioritize your security and peace of mind at every stage.',
  };

  const fallbackSteps = [
    {
      icon: { value: '' },
      title: { value: translate('newBuyer.confidence.feature1Title') || 'Verified Vehicles' },
      description: { value: translate('newBuyer.confidence.feature1Desc') || 'Every vehicle undergoes rigorous inspection.' },
    },
    {
      icon: { value: '' },
      title: { value: translate('newBuyer.confidence.feature2Title') || 'Secure Payments' },
      description: { value: translate('newBuyer.confidence.feature2Desc') || 'Protected transactions with escrow.' },
    },
    {
      icon: { value: '' },
      title: { value: translate('newBuyer.confidence.feature3Title') || 'Full Documentation' },
      description: { value: translate('newBuyer.confidence.feature3Desc') || 'Complete paperwork handled seamlessly.' },
    },
  ];

  // Use props if available, else fallback
  const finalHeader = header && header.title ? header : fallbackHeader;
  const finalSteps = steps && steps.length > 0 ? steps : fallbackSteps;

  return (
    <section className="bg-page py-8 sm:py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Outer Container Card */}
        <div className="bg-primary-dark/30 border border-white/5 rounded-[40px] p-8 sm:p-12 lg:p-20 relative overflow-hidden">
          <div className='flex'>
            {/* Large Shield Background Icon (Watermark effect) */}
            <div className="md:relative absolute bottom-6 opacity-[0.03] pointer-events-none">
              <ShieldCheck size={200} strokeWidth={1} className="text-white" />
            </div>

            {/* Dynamic Header Section */}
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
                {finalHeader.title}
              </h2>
              <p 
                className="text-gray-500 text-base sm:text-lg font-medium leading-relaxed"
                dangerouslySetInnerHTML={{ __html: finalHeader.description }}
              />
            </div>
          </div>

          {/* Dynamic Feature Grid */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-12">
            {finalSteps.map((step, idx) => {
              const Icon = getFallbackIcon(step.title?.value || '', idx);
              const hasCustomIcon = step.icon?.value && step.icon.value.trim() !== '';

              return (
                <div 
                  key={idx} 
                  className="bg-page border border-white/5 rounded-[24px] p-8 lg:p-10 flex flex-col hover:border-white/10 transition-all duration-300"
                >
                  {/* Dynamic Icon Container (image or fallback) */}
                  <div className="w-12 h-12 bg-[#1d63a3]/10 rounded-xl flex items-center justify-center mb-8 border border-[#1d63a3]/20 overflow-hidden">
                    {hasCustomIcon ? (
                      <img
                        src={step.icon.value}
                        alt={step.title?.value || ''}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'; // Hide broken image
                          e.currentTarget.nextSibling?.style.removeProperty('display'); // Show fallback icon
                        }}
                      />
                    ) : null}

                    <Icon
                      className="w-6 h-6 text-primary"
                      strokeWidth={2}
                      style={{ display: hasCustomIcon ? 'none' : 'block' }} // Hide if image is shown
                    />
                  </div>

                  {/* Dynamic Text Content */}
                  <h3 className="text-white font-bold text-xl mb-4 tracking-tight">
                    {step.title?.value || fallbackSteps[idx].title.value}
                  </h3>
                  <p className="text-description text-[15px] leading-relaxed font-medium">
                    {step.description?.value || fallbackSteps[idx].description.value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  )
}